/**
 * useEntityDraftPreview — aperçu TEMPS RÉEL d'une adhésion (/a) ou d'un
 * événement (/b) cadré en iframe par le dashboard.
 *
 * La page publique est la SEULE source de vérité de l'aperçu : le dashboard ne
 * redessine pas une copie de la mise en page. C'est ce que font les vieilles
 * pages d'aperçu du dashboard, qui ont fini par diverger de la vraie page —
 * cf. l'audit PayPal/Stripe de l'aperçu de cagnotte. Ici, on affiche la page
 * réelle et on y injecte le BROUILLON en cours d'édition.
 *
 * Deux sources, par priorité décroissante et UNIQUEMENT quand `?preview=true` :
 *
 *   1. `postMessage` live `preview-update` depuis le dashboard parent
 *   2. les données SSR (ce qui est enregistré en base)
 *
 * Hors mode aperçu, le composable est inerte : il renvoie les données SSR
 * telles quelles et n'installe aucun écouteur.
 *
 * ⚠️ Pas de query params `previewXxx` ici, contrairement au canal des liens de
 * paiement : un brouillon d'adhésion (tarifs, options, champs personnalisés)
 * ne tient pas dans une URL. Le premier rendu montre donc l'état ENREGISTRÉ,
 * puis le handshake `preview-ready` → `preview-update` le remplace par le
 * brouillon dans la foulée.
 *
 * Karim 2026-08-30.
 */
import { ref, computed, type ComputedRef } from "vue";

/** Origine du dashboard en production — fallback quand la config est vide. */
const PROD_DASHBOARD_ORIGIN = "https://dashboard.donoor.org";

/**
 * SÉCURITÉ — origines autorisées à piloter l'aperçu.
 *
 * Identique au canal des liens de paiement : sans ce filtre, n'importe quelle
 * page tierce qui embarquerait l'iframe pourrait injecter le contenu affiché
 * (prix, titre, description) — donc mentir au futur adhérent. On ne renvoie
 * que des ORIGINES (protocole + host + port), jamais un chemin, car c'est ce
 * que porte `event.origin`.
 *
 * En prod `dashboardUrl` est volontairement vide (URLs relatives via rewrite
 * Vercel) → fallback sur l'origine prod en dur. En dev il vaut par ex.
 * "http://localhost:3000" et passe en tête.
 */
function resolveAllowedDashboardOrigins(dashboardUrl: string): string[] {
  const origins: string[] = [];
  const trimmed = dashboardUrl.trim();
  if (trimmed) {
    try {
      origins.push(new URL(trimmed).origin);
    } catch {
      // dashboardUrl mal formé → seul le fallback prod est retenu.
    }
  }
  if (!origins.includes(PROD_DASHBOARD_ORIGIN)) {
    origins.push(PROD_DASHBOARD_ORIGIN);
  }
  return origins;
}

/**
 * Brouillon poussé par le dashboard. Volontairement permissif : la forme exacte
 * appartient à l'entité (adhésion ou événement), et le dashboard envoie le
 * même objet que celui qu'il enverrait à l'API. La page publique n'en lit que
 * ce qu'elle sait afficher.
 */
export interface EntityDraftOverrides {
  type?: string;
  /** L'entité elle-même (plan d'adhésion ou événement). */
  entity?: Record<string, unknown> | null;
  /**
   * Liste ordonnée des enfants de l'entité : tarifs d'adhésion, types de
   * billets, ou produits d'une boutique. Un seul nom pour les trois — la
   * fusion est identique (appariement par `id`).
   */
  tarifs?: Record<string, unknown>[] | null;
  /** Élément à amener à l'écran (`[data-preview-id]`). */
  scrollTo?: string;
}

export interface UseEntityDraftPreviewOptions {
  /** `?preview=true` — désactive tout quand `false`. */
  isPreviewMode: ComputedRef<boolean>;
  /**
   * Le dashboard demande de SIMULER un événement entrant (aujourd'hui : un don
   * sur une cagnotte). La page l'applique par son chemin RÉEL, pour montrer
   * exactement ce que verra un visiteur — une démonstration qui emprunterait un
   * chemin séparé finirait par mentir. Karim 2026-08-30.
   */
  onSimulate?: (payload: Record<string, unknown>) => void;
}

export function useEntityDraftPreview(opts: UseEntityDraftPreviewOptions) {
  const config = useRuntimeConfig();
  const { isPreviewMode } = opts;

  const allowedOrigins = resolveAllowedDashboardOrigins(
    (config.public.dashboardUrl as string) || "",
  );

  const overrides = ref<EntityDraftOverrides>({});

  // Masque la barre de défilement du document CADRÉ : dans le châssis mobile
  // (260 px de large), celle du système mange une part visible de la largeur
  // et fausse le rendu. Le style vit dans `main.css` ; la page publique
  // normale n'est jamais concernée. Karim 2026-08-30.
  useHead({
    htmlAttrs: {
      class: computed(() => (isPreviewMode.value ? "is-dashboard-preview" : "")),
    },
  });

  if (import.meta.client) {
    window.addEventListener("message", (event: MessageEvent) => {
      // SÉCURITÉ — n'accepter les mises à jour que du dashboard de confiance.
      if (!allowedOrigins.includes(event.origin)) return;
      const data = event.data as (EntityDraftOverrides & {
        simulate?: Record<string, unknown>;
      }) | undefined;

      // Simulation demandée par l'aperçu — traitée AVANT les mises à jour de
      // brouillon, dont elle est indépendante.
      if (data?.type === "preview-simulate" && data.simulate) {
        opts.onSimulate?.(data.simulate);
        return;
      }

      if (data?.type !== "preview-update") return;
      overrides.value = { ...data };
      if (data.scrollTo) {
        void nextTick(() => {
          const el = document.querySelector(
            `[data-preview-id="${data.scrollTo}"]`,
          );
          if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      }
    });

    // Signale au parent qu'on est prêt à recevoir le brouillon.
    // SÉCURITÉ — cibler l'origine du dashboard, jamais "*" : un parent
    // malveillant ne doit pas savoir qu'une page d'aperçu est chargée.
    if (isPreviewMode.value) {
      const primaryOrigin = allowedOrigins[0] ?? PROD_DASHBOARD_ORIGIN;
      window.parent.postMessage({ type: "preview-ready" }, primaryOrigin);
    }
  }

  /**
   * Fusionne le brouillon sur l'entité enregistrée.
   *
   * Fusion de SURFACE et non remplacement : le brouillon du dashboard ne porte
   * que les champs éditables (titre, description, prix…), pas ceux calculés
   * par l'API (compteurs d'adhérents, organisation, image résolue). Un
   * remplacement pur ferait donc disparaître la moitié de la page.
   */
  function mergeEntity<T>(ssr: T | null): T | null {
    if (!isPreviewMode.value) return ssr;
    const draft = overrides.value.entity;
    if (!draft) return ssr;
    if (!ssr) return draft as T;
    return { ...ssr, ...draft } as T;
  }

  /**
   * Liste des tarifs : le brouillon REMPLACE la liste enregistrée quand il en
   * fournit une — sinon un tarif supprimé dans le dashboard continuerait
   * d'apparaître dans l'aperçu.
   *
   * Chaque tarif du brouillon est tout de même fusionné sur son homologue en
   * base (même `id`), pour conserver les champs calculés côté serveur (places
   * restantes, compteur d'adhérents).
   */
  function mergeTarifs<T extends { id?: unknown }>(ssr: T[]): T[] {
    if (!isPreviewMode.value) return ssr;
    const draft = overrides.value.tarifs;
    if (!draft) return ssr;
    const byId = new Map(ssr.map((t) => [String(t.id), t]));
    return draft.map((d) => {
      const base = byId.get(String(d.id));
      return (base ? { ...base, ...d } : d) as T;
    });
  }

  /** Le brouillon a-t-il déjà été reçu ? (évite un flash sur l'enregistré) */
  const hasDraft = computed(
    () => isPreviewMode.value && overrides.value.entity != null,
  );

  return { overrides, mergeEntity, mergeTarifs, hasDraft };
}
